import React, { useEffect, useState, useRef } from "react";
import api from "../api.js";
import LoadingIndicator from "../components/LoadingIndicator";

// Modal styling (move this to your CSS/SCSS or use Tailwind as needed)
const overlayStyle = {
  position: "fixed",
  top: 0, left: 0, width: "100vw", height: "100vh",
  background: "rgba(36,36,38,0.6)",
  zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center",
};

const cardStyle = {
  background: "#fff",
  borderRadius: "18px",
  boxShadow: "0 8px 36px rgba(0,0,0,0.16)",
  padding: "2.4rem 1.8rem 2.2rem 1.8rem",
  minWidth: 340, maxWidth: 450, width: "95vw",
  fontFamily: "inherit",
  position: "relative",
  animation: "fadeIn 0.18s",
};

const closeBtnStyle = {
  position: "absolute",
  top: 18, right: 20,
  fontSize: "1.8rem",
  color: "#b9bcc6",
  background: "none",
  border: "none",
  cursor: "pointer",
  transition: "color 0.16s",
};

const headingStyle = {
  fontWeight: 700, fontSize: "1.17rem", marginBottom: 10,
  letterSpacing: "-0.02em",
  display: "flex", alignItems: "center", gap: 8,
};
const subStyle = {
  color: "#666",
  margin: "8px 0 18px 0",
  fontSize: "1.04rem",
  lineHeight: 1.38,
};
const inputStyle = {
  fontSize: "1rem",
  width: "100%",
  border: "1.2px solid #e5e7eb",
  borderRadius: "11px",
  padding: "0.75rem 0.98rem",
  marginBottom: "13px",
  background: "#fafbfc",
  resize: "vertical",
  minHeight: "38px",
  outline: "none",
  boxSizing: "border-box",
};
const btnStyle = {
  background: "#19b47b",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.78em 1.44em",
  fontWeight: 600,
  fontSize: "1.04rem",
  marginTop: 6,
  cursor: "pointer",
  transition: "background 0.16s",
  letterSpacing: "-0.01em",
};
const btnDisabledStyle = {
  ...btnStyle,
  opacity: 0.48,
  cursor: "not-allowed",
};

const errorStyle = {
  color: "#e35b5b",
  margin: "0 0 10px 0",
  fontSize: "0.96rem",
};

const progressStyle = {
  fontSize: "0.97rem", color: "#8e8e96",
  marginBottom: "5px", fontWeight: 500, letterSpacing: "-0.01em",
  display: "block",
};

const blendStyle = {
  background: "#f7f8fa",
  borderRadius: "10px",
  fontSize: "1.06rem",
  color: "#53535a",
  padding: "9px 11px",
  marginBottom: "10px",
  fontWeight: 500,
  wordBreak: "break-word"
};

const introText = (
  <>
    <span style={headingStyle}>Welcome to Vybly <span role="img" aria-label="wave">👋</span></span>
    <div style={subStyle}>
      We hope you found the intro video useful. Now, let’s get to know you a bit better—so we can help you unlock what matters most.<br /><br />
      You'll answer <b>3 short questions</b>. Please answer honestly, even if it feels a little different from other apps. There’s no “right” answer.
    </div>
  </>
);

export default function OnboardingQuestionsModal({ onClose }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [step, setStep] = useState(0); // 0 = intro, 1/2/3 = Qs, 4 = done
  const [answers, setAnswers] = useState(["", "", ""]);
  const [questions, setQuestions] = useState(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [blend, setBlend] = useState([]);
  const [error, setError] = useState("");

    // -------------- Generate tabs automatically on completion --------------
    useEffect(() => {
    if (step === 4) {
        // Only trigger ONCE (prevents double call if component re-renders)
        if (answers.every(a => a.trim().length > 0)) {
        // Fire-and-forget; no spinner, no user-blocking UI
        const token = localStorage.getItem("token");
        api.post(
            `${process.env.REACT_APP_API_BASE_URL}/tabs/generateFromOnboarding`,
            {
            onboarding_questions: questions,
            onboarding_answers: answers,
            },
            { headers: { Authorization: `Bearer ${token}` } }
        ).catch((err) => {
            // Optionally: log or track this error
            console.error("Tab generation error:", err);
        });
        }
    }
    // eslint-disable-next-line
    }, [step]);

  // On open, fetch the first question
  useEffect(() => {
    if (step === 1 && !questions[0] && !loading) {
      fetchQuestion(1);
    }
    // eslint-disable-next-line
  }, [step]);

  // Fetch question (handles Q1 static, Q2/3 dynamic)
  const fetchQuestion = async (questionNumber, onboardingAnswer1 = "", onboardingAnswer2 = "", onboardingAnswer3 = "") => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await api.post(
        `${API_BASE_URL}/api/onboarding/question`,
        {
          question_number: questionNumber,
          onboarding_answer_1: onboardingAnswer1,
          onboarding_answer_2: onboardingAnswer2,
          onboarding_answer_3: onboardingAnswer3,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data && res.data.onboarding_question) {
        setQuestions(prev => {
          const copy = [...prev];
          copy[questionNumber - 1] = res.data.onboarding_question;
          return copy;
        });
        if (res.data.blend && res.data.blend.length > 0) setBlend(res.data.blend);
      } else {
        throw new Error("Invalid question response from server.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "An error occurred. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Advance to next step/question
  const handleNext = async () => {
    setError("");
    if (step === 0) {
      setStep(1);
      return;
    }
    if (answers[step - 1].trim() === "") {
      setError("Please write an honest answer before continuing.");
      return;
    }
    if (step === 1) {
      await fetchQuestion(2, answers[0]);
      setStep(2);
    } else if (step === 2) {
      await fetchQuestion(3, answers[0], answers[1]);
      setStep(3);
    } else if (step === 3) {
      // Save last answer (Q3) and finish
      await fetchQuestion(3, answers[0], answers[1], answers[2]);
      setStep(4);
    }
  };

  // For input
  const handleChange = (idx, val) => {
    setAnswers(prev => {
      const copy = [...prev];
      copy[idx] = val;
      return copy;
    });
    setError("");
  };

  // Close allowed only after finishing or at intro screen
  const canClose = step === 0 || step === 4;

  // Handle close (optionally force complete onboarding: only allow close at step 4)
  const doClose = () => {
    if (canClose) onClose();
  };

  // Blend description string
  const blendStr = blend.length === 2 ? `${blend[0]} + ${blend[1]}` : blend.join(" + ");

  // Content render
  let content = null;
  if (step === 0) {
    content = (
      <>
        {introText}
        <button
          style={btnStyle}
          onClick={handleNext}
        >Let’s get started →</button>
      </>
    );
  } else if (step >= 1 && step <= 3) {
    content = (
      <>
        <div style={{ fontWeight: 600, fontSize: "1.09rem", margin: "12px 0 6px 0" }}>
          {loading ? <LoadingIndicator /> : questions[step - 1]}
        </div>
        <textarea
          style={inputStyle}
          rows={3}
          value={answers[step - 1]}
          placeholder="Type your answer here..."
          onChange={e => handleChange(step - 1, e.target.value)}
          disabled={loading}
        />
        {error && <div style={errorStyle}>{error}</div>}
        <button
          style={
            answers[step - 1].trim() && !loading
              ? btnStyle
              : btnDisabledStyle
          }
          disabled={!answers[step - 1].trim() || loading}
          onClick={handleNext}
        >
          {step === 3 ? "Finish" : "Next"}
        </button>
      </>
    );
  } else if (step === 4) {
    content = (
      <>
        <div style={headingStyle}>You're all set <span role="img" aria-label="confetti">🎉</span></div>
        <div style={subStyle}>
          Thank you for sharing honestly. Your answers will help us surface the best insights and tabs for you.<br /><br />
        </div>
        <button style={btnStyle} onClick={doClose}>Continue →</button>
      </>
    );
  }

  const modalRef = useRef(null);

return (
  <>
    {/* Loading overlay always on top, hides everything else */}
    {loading ? (
      <div style={{
        position: "fixed",
        top: 0, left: 0, width: "100vw", height: "100vh",
        zIndex: 10010,
        background: "rgba(240,240,242,0.68)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <LoadingIndicator />
      </div>
    ) : (
      <div
        className="vybly-modal-overlay onboarding-modal-overlay"
        style={{ zIndex: 10002 }}
      >
        <div className="vybly-modal-card onboarding-modal-card">
          {canClose && (
            <button style={closeBtnStyle} aria-label="Close" onClick={doClose}>
              &times;
            </button>
          )}
          {content}
        </div>
        <style>{`
          .vybly-modal-overlay.onboarding-modal-overlay {
            position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
            background: #20283014; z-index: 9999;
            display: flex; align-items: center; justify-content: center;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
            padding-top: 0;
          }
          .vybly-modal-card.onboarding-modal-card {
            position: relative;
            background: #fff;
            border-radius: 26px;
            box-shadow: 0 10px 44px #d1e2f144, 0 1.5px 13px #beeaff33;
            padding: 42px 48px 22px 48px;
            min-width: 340px;
            max-width: 90vw;
            width: 100%;
            max-height: 97vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            overflow: visible !important;
          }
          @media (max-width: 900px) {
            .vybly-modal-card.onboarding-modal-card {
              min-width: 94vw; max-width: 99vw; padding: 10px 1vw 12px 1vw;
            }
          }
          @media (max-width: 640px) {
            .vybly-modal-card.onboarding-modal-card {
              width: 96vw;
              min-width: unset;
              max-width: 90vw;
              border-radius: 13px;
              padding: 12vw 2vw 8vw 2vw;
              box-sizing: border-box;
            }
          }
        `}</style>
      </div>
    )}
  </>
);

}